import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FilterPanel = ({ onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '90',
    department: 'all',
    jobLevel: 'all',
    hiringManager: 'all',
    compareMode: false,
    comparePeriod: 'previous'
  });

  const dateRangeOptions = [
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '180', label: 'Last 6 months' },
    { value: '365', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'product', label: 'Product' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' },
    { value: 'design', label: 'Design' },
    { value: 'operations', label: 'Operations' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' }
  ];

  const jobLevelOptions = [
    { value: 'all', label: 'All Levels' },
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (5-8 years)' },
    { value: 'lead', label: 'Lead/Principal (8+ years)' },
    { value: 'executive', label: 'Executive/C-Level' }
  ];

  const hiringManagerOptions = [
    { value: 'all', label: 'All Managers' },
    { value: 'sarah-johnson', label: 'Sarah Johnson (Engineering)' },
    { value: 'mike-chen', label: 'Mike Chen (Product)' },
    { value: 'emily-davis', label: 'Emily Davis (Marketing)' },
    { value: 'alex-rodriguez', label: 'Alex Rodriguez (Sales)' },
    { value: 'lisa-wang', label: 'Lisa Wang (Design)' }
  ];

  const comparePeriodOptions = [
    { value: 'previous', label: 'Previous period' },
    { value: 'quarter', label: 'Same quarter last year' },
    { value: 'year', label: 'Same period last year' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: '90',
      department: 'all',
      jobLevel: 'all',
      hiringManager: 'all',
      compareMode: false,
      comparePeriod: 'previous'
    };
    setFilters(defaultFilters);
    onFiltersChange?.(defaultFilters);
  };

  const toggleCompareMode = () => {
    const newCompareMode = !filters?.compareMode;
    const newFilters = { ...filters, compareMode: newCompareMode };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  return (
    <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl shadow-soft">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200/60">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-xl">
            <Icon name="Filter" size={20} className="text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Analytics Filters</h3>
            <p className="text-sm text-slate-500">Customize your hiring insights view</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Compare Mode Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl">
            <Icon name="BarChart3" size={16} className="text-slate-600" />
            <span className="text-sm text-slate-700">Compare</span>
            <button
              onClick={toggleCompareMode}
              className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                filters?.compareMode ? 'bg-indigo-600' : 'bg-slate-300'
              }`}
            >
              <div
                className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform duration-200 ${
                  filters?.compareMode ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="text-slate-600"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>

      {/* Filters Content */}
      {isExpanded && (
        <div className="p-6 space-y-6">
          {/* Primary Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Time Period</label>
              <Select
                options={dateRangeOptions}
                value={filters?.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
                className="bg-white/60"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Department</label>
              <Select
                options={departmentOptions}
                value={filters?.department}
                onChange={(value) => handleFilterChange('department', value)}
                className="bg-white/60"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Job Level</label>
              <Select
                options={jobLevelOptions}
                value={filters?.jobLevel}
                onChange={(value) => handleFilterChange('jobLevel', value)}
                className="bg-white/60"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Hiring Manager</label>
              <Select
                options={hiringManagerOptions}
                value={filters?.hiringManager}
                onChange={(value) => handleFilterChange('hiringManager', value)}
                className="bg-white/60"
              />
            </div>
          </div>

          {/* Compare Mode Settings */}
          {filters?.compareMode && (
            <div className="p-4 bg-indigo-50/60 border border-indigo-200/60 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="GitCompare" size={16} className="text-indigo-600" />
                <span className="text-sm font-medium text-indigo-900">Compare Mode Settings</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Compare Against</label>
                  <Select
                    options={comparePeriodOptions}
                    value={filters?.comparePeriod}
                    onChange={(value) => handleFilterChange('comparePeriod', value)}
                    className="bg-white/60"
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-xs text-indigo-700 bg-indigo-100/60 px-3 py-2 rounded-lg">
                    📊 Charts will show comparison data overlays
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-slate-200/60">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                iconName="RotateCcw"
                iconPosition="left"
                className="bg-white/60"
              >
                Reset All
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Save"
                iconPosition="left"
                className="bg-white/60"
              >
                Save View
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Share2"
                iconPosition="left"
                className="bg-white/60"
              >
                Share Report
              </Button>
            </div>
            
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Live data • Updated 3 min ago</span>
              </div>
              <div className="hidden sm:flex items-center gap-1">
                <Icon name="Database" size={14} />
                <span>2,847 records</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;